/**
 * axios 请求模块
 * @module axios
 * @example
 *
 * axios({
 *   url: '<url>',
 *   method: 'get',
 *   data: {query: 1} // get和post都可以用data
 * })
 *
 * // 开启缓存
 * axios({
 *   url: '<url>',
 *   settings: {
 *     cache: true
 *   }
 * })
 *
 * // 显示错误提示
 * axios({
 *   url: '<url>',
 *   settings: {
 *     failMessage: true
 *   }
 * })
 *
 * // 关闭校验success
 * axios({
 *   url: '<url>',
 *   settings: {
 *     validSuccess: false
 *   }
 * })
 */
import axios from 'axios';
import { Message, Loading, Notification } from 'element-ui';
import { save, get } from './storage';
import { stringify } from './url';
import { pick } from './util';
// import {defaultRemote} from './appUtils';

// 可以发送data的请求类型
const DATA_METHODS = ['put', 'post', 'patch'];

// ELMessage组件实例
let messageInstance = null;

// ElLoading 实例存储数组
const loadingInstanceArray = [];

// 请求设置默认选项
const defaultSetting = {
  // 开启缓存返回数据
  cache: false,
  // 自定义缓存key
  cacheKey: null,
  // data转换成FormData
  formData: true,
  // 请求失败时显示错误提示
  failMessage: false,
  // 是否校验响应success字段
  validSuccess: true,
  // 显示loading
  loading: false,
  // 剔除null、undefined 空字符串的参数
  picked: true,
  // 自定义剔除函数
  pickFilter: v => v !== null && v !== undefined && v !== '',
  // `baseURL` 将自动加在 `url` 前面，除非 `url` 是一个绝对 URL。
  baseURL: null,
  // 表示跨域请求时是否需要使用凭证
  withCredentials: false,
  // 发送数据内容Content-Type，form / json / data
  type: 'form',
  // 响应返回原始对象
  originResponse: false,
  // 支持用户自动取消
  cancelable: false,
  // 显示取消按钮时间
  cancelVisibleTime: 10 * 1000,
  // 取消提示组件默认配置
  cancelNotifyConfig: null,
  // 取消请求时，真实取消发起的请求，设置为false，不会真正取消请求，只是关闭loading
  doCancelRequest: true,
  // 消息提示采用Notification组件
  notify: false
};

/**
 * 请求、响应函数句柄，销毁时需要用到
 */
let requestId, responseId;

const cancelMessage = 'user cancel';
const source = axios.CancelToken.source();

function toFormData(data = {}, type = 'data') {
  if (type !== 'data' && typeof data === 'object') {
    return stringify(data);
  } else {
    if (data instanceof FormData) return data;
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });
    return formData;
  }
}

function createCacheKey(url, params) {
  return url + stringify(params);
}

function showMessage(notify, type, message) {
  const MessageClass = notify ? Notification : Message;
  messageInstance && messageInstance.close();
  messageInstance = MessageClass({
    type,
    duration: 5 * 1000,
    message
  });
}

function doResolve(cacheKey, data, resolve) {
  if (cacheKey) {
    save(cacheKey, data);
  }
  resolve(data);
  return data;
}

function doReject(notify, failMessage, res, message, reject) {
  if (failMessage) {
    showMessage(notify, 'warning', message);
  }
  reject(res);
  return res;
}

function processSuccess(cacheKey, res, resolve, reject, {notify, failMessage, validSuccess, originResponse}) {
  const prefix = res?.config?.url || '-';
  if (res instanceof Error) {
    let message = res.message || '未知错误';
    message = `[${prefix}] ` + message;
    /*const json = {url: window.location.href, info: res, message, stack: res.stack || ''};
    defaultRemote(json);*/
    return doReject(notify, failMessage, res, message, reject);
  }
  const data = res.data || {};
  if (data.promise) {
    return resolve(data.promise);
  }
  // 不需要检查判断 success 标识
  if (!validSuccess) {
    return doResolve(cacheKey, originResponse ? res : data, resolve);
  }
  // 需要判断 success
  if (data.success) {
    return doResolve(cacheKey, originResponse ? res : data.data, resolve);
  } else {
    data.message = data.message || data.msg || '未知错误';
    data.message = `[${prefix}] ` + data.message;
    return doReject(notify, failMessage, originResponse ? res : data, data.message, reject);
  }
}

function processError(error = {}, reject, {failMessage, notify}) {
  if (error && error.message === cancelMessage) return reject(error);
  const prefix = error?.config?.url || '-';
  const data = error?.response?.data;
  if (data && data.code === 500) {
    error.message = error.msg = '系统繁忙，请稍后重试！';
  }
  if (data && data.message) {
    error.message = error.msg = data.message.replace(/^\[4:ReqFailure]/, '');
  }
  if (error) {
    const message = `[${prefix}] ` + (error.message || '未知错误');
    /*const json = {url: window.location.href, message, stack: error.stack || '', info: error};
    defaultRemote(json);*/
  }
  if (failMessage) {
    error.message = `[${prefix}] ` + error.message;
    showMessage(notify, 'error', error.message);
  }
  reject(error);
}

function pickData(data, picked, pickFilter) {
  // 只对对象进行过滤
  if (!data || Array.isArray(data)) return data;
  return picked ? pick(data, pickFilter) : data;
}

function getHeaders(headers, type) {
  const types = {
    form: 'application/x-www-form-urlencoded',
    json: 'application/json',
    data: 'multipart/form-data'
  };
  const {href, host, protocol} = location;
  const domain = `${protocol}//${host}`;
  const url = href.replace(domain, '');
  return {
    'Content-Type': types[type] || types.form,
    'Page-Url': url,
    ...headers
  };
}

function isJsonType(headers = {}) {
  return !!Object.entries(headers).find(([k, v]) => {
    return k.toLowerCase() === 'content-type' && v.includes('application/json');
  });
}

function showCancelNotify(config, onClick) {
  return Notification({
    type: 'info',
    title: '提示',
    duration: 0,
    dangerouslyUseHTMLString: true,
    showClose: false,
    message: `<div style="font-size:13px;"><span style="display: block;">当前页面数据量大或网络不给力，请稍等!</span>
 <span style="float:right; margin-top:5px; background: #0068C8; line-height:1; cursor: pointer; color:#fff; padding: 5px 10px; border-radius: 4px;">
 停止加载
 </span>
 </div>`,
    onClick,
    ...config
  });
}

/**
 * axios 实例
 * @member instance
 */
export const instance = axios.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  timeout: 60 * 1000,
  cancelToken: source.token
});

function ajax(config = {}) {
  const {url, method = 'get', data, params, settings, headers} = config;
  const {
    formData,
    cache,
    cacheKey,
    failMessage,
    validSuccess,
    loading,
    picked,
    pickFilter,
    baseURL,
    withCredentials,
    type,
    originResponse,
    cancelable,
    cancelVisibleTime,
    cancelNotifyConfig,
    doCancelRequest,
    notify
  } = Object.assign({}, defaultSetting, settings || {});
  if (!url) {
    throw new Error('options必须要定义url参数');
  }

  let transformData = null;
  let query = null;
  // 'put', 'post', 'patch' 方法时 把data转化成FormData
  if (DATA_METHODS.includes(method.toLowerCase())) {
    // 发送内容是json时不需要转FormData
    const isJson = (type === 'json' || isJsonType(headers));
    transformData = (formData && !isJson) ? toFormData(pickData(data, picked, pickFilter), type) : pickData(data, picked, pickFilter);
  } else {
    // 其它方法如 get， 优先取params，通过url传递
    query = pickData(params || data, picked, pickFilter);
  }
  const realCacheKey = cache ? (cacheKey || createCacheKey(url, query || data)) : '';
  // 如果配置接口缓存，检测是否缓存数据返回，不发起请求
  if (realCacheKey) {
    const info = get(realCacheKey);
    if (info) {
      return Promise.resolve(info);
    }
  }
  let cancelTipTimer = null;
  let cancelNotify = null;
  let cancelTokenSource = null;
  return new Promise((resolve, reject) => {
    if (loading) {
      // 记录el-loading 实例
      const loadingInstance = Loading.service(loading);
      if (!loadingInstance.$$mousedownListener) {
        loadingInstance.$$mousedownListener = (event) => {
          event.stopPropagation();
        };
        loadingInstance.$el.addEventListener('mousedown', loadingInstance.$$mousedownListener, true);
      }
      loadingInstanceArray.push(loadingInstance);
    }
    // 支持用户取消请求
    if (cancelable) {
      cancelTokenSource = axios.CancelToken.source();
      cancelTipTimer = setTimeout(() => {
        cancelNotify = showCancelNotify(cancelNotifyConfig, e => {
          if (doCancelRequest) {
            cancelTokenSource?.cancel(cancelMessage);
          } else {
            const loadingInstance = loadingInstanceArray.pop();
            if (loadingInstance && loadingInstanceArray.length === 0) {
              loadingInstance.close();
            }
          }
          cancelNotify.close();
        });
      }, cancelVisibleTime);
    }
    delete config.settings;
    instance({
      baseURL,
      withCredentials,
      ...config,
      headers: getHeaders(headers, type),
      url,
      method,
      data: transformData,
      params: query,
      cancelToken: cancelTokenSource?.token
    }).then(res => processSuccess(realCacheKey, res, resolve, reject, {
      notify,
      failMessage,
      validSuccess,
      originResponse
    }))
      .catch(e => processError(e, reject, {
        notify,
        failMessage,
        validSuccess
      }))
      .finally(_ => {
        const loadingInstance = loadingInstanceArray.pop();
        // 销毁el-loading组件, 实例是共享的，只需close一个就可以
        if (loadingInstance && loadingInstanceArray.length === 0) {
          // 如果在这里解除监听，在快速点击情况下仍然有可能拦截失败
          // if (loadingInstance.$$mousedownListener) {
          //   loadingInstance.$el.removeEventListener('mousedown', loadingInstance.$$mousedownListener, true);
          //   delete loadingInstance.$$mousedownListener;
          // }
          loadingInstance.close();
        }
        cancelTokenSource = null;
        cancelNotify && cancelNotify.close();
        cancelTipTimer && clearTimeout(cancelTipTimer);
      });
  });
}

/**
 * 设置请求拦截
 * @param {function} success 成功拦截函数
 * @param {function} fail 失败拦截函数
 * @returns {number}
 */
export function setRequest(success, fail) {
  const request = instance.interceptors.request;
  if (requestId) {
    request.eject(requestId);
  }
  requestId = request.use(success, fail);
  return requestId;
}

/**
 * 设置响应拦截
 * @param {function} success 成功拦截函数
 * @param {function} fail 失败拦截函数
 * @returns {number}
 */
export function setResponse(success, fail) {
  const response = instance.interceptors.response;
  if (responseId) {
    response.eject(responseId);
  }
  responseId = response.use(success, fail);
  return responseId;
}

/**
 * 取消请求
 * @param {string} message 描述信息
 */
export function cancel(message) {
  source.cancel(message);
}

/**
 * 更改默认配置项
 * @param {object} settings
 */
export function setConfig(settings = {}) {
  Object.assign(defaultSetting, settings);
}

/**
 * 发请求函数，参数和axios一致，扩展了settings设置参数
 * @function ajax
 * @param {object} [settings] 对axios扩展的功能设置
 * @param {boolean} [settings.cache=false] 开启缓存返回数据
 * @param {string} [settings.cacheKey] 自定义缓存key，默认自动生成
 * @param {boolean} [settings.formData=true] post数据data转换成FormData
 * @param {boolean} [settings.failMessage] 请求失败时显示错误提示
 * @param {boolean} [settings.validSuccess=true] 是否校验响应success字段
 * @param {boolean} [settings.loading] 显示loading
 * @param {boolean} [settings.picked=true] 开启剔除null、undefined、空字符串的参数
 * @param {function} [settings.pickFilter] 自定义剔除函数，默认 null、undefined、空字符串的参数
 * @param {string} [settings.baseURL] baseURL 将自动加在 url 前面，除非 url 是一个绝对 URL。
 * @param {boolean} [settings.withCredentials] 跨域请求时是否需要使用凭证
 * @param {string} [settings.type=form] 发送数据内容Content-Type，form / json / data
 * @param {number} [settings.originResponse=false] 响应返回原始对象
 * @param {boolean} [config.cancelable=true] 开启请求取消功能
 * @param {number} [config.cancelVisibleTime=10x1000] 显示取消提示时间
 * @param {object} [config.cancelNotifyConfig] 取消提示组件配置
 * @returns {Promise}
 */
export default ajax;

/**
 * 创建多个请求接口
 * @param {Object} config 配置对象 ｛ name: options ｝
 * @return {Object}
 */
export function createMap(config = {}) {
  const map = {};
  Object.entries(config).forEach(([name, opts]) => {
    const options = typeof opts === 'string' ? {url: opts} : opts;
    map[name] = data => ajax({
      ...options,
      data
    });
  });
  return map;
}
